/**
 * Turn signalling (§12).
 *
 * Everyone is looking at a video call in another window, so becoming the active
 * player has to be noticeable without the game tab being visible: the document
 * title changes, the favicon flashes, and a short sound plays.
 */

const BASE_TITLE = "Liar's Dice";
const ALERT_TITLE = '(!) Your turn — Liar\'s Dice';

const MUTE_KEY = 'ld_muted';

function faviconLink(): HTMLLinkElement {
  const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (existing !== null) return existing;

  const link = document.createElement('link');
  link.rel = 'icon';
  document.head.appendChild(link);
  return link;
}

function faviconDataUri(background: string, pips: string): string {
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32"><rect width="32" height="32" rx="7" fill="${background}"/><g fill="${pips}"><circle cx="10" cy="10" r="3"/><circle cx="22" cy="10" r="3"/><circle cx="16" cy="16" r="3"/><circle cx="10" cy="22" r="3"/><circle cx="22" cy="22" r="3"/></g></svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}

const IDLE_ICON = faviconDataUri('#e9e9ef', '#12131a');
const ALERT_ICON = faviconDataUri('#ffb020', '#12131a');

export class TurnSignal {
  #flashTimer: number | null = null;
  #alerting = false;
  #audio: AudioContext | null = null;
  #muted = localStorage.getItem(MUTE_KEY) === '1';

  get muted(): boolean {
    return this.#muted;
  }

  setMuted(muted: boolean): void {
    this.#muted = muted;
    localStorage.setItem(MUTE_KEY, muted ? '1' : '0');
  }

  /**
   * Mobile WebKit refuses to start audio outside a user gesture, so the first
   * real click unlocks the context. Called from a click handler, never on load.
   */
  unlockAudio(): void {
    if (this.#audio !== null) return;
    try {
      this.#audio = new AudioContext();
      void this.#audio.resume();
    } catch {
      this.#audio = null;
    }
  }

  /** Called on every view; only fires on the transition into your turn. */
  update(isYourTurn: boolean): void {
    if (isYourTurn === this.#alerting) return;
    this.#alerting = isYourTurn;

    if (isYourTurn) {
      this.#startFlashing();
      this.#chime();
    } else {
      this.#stopFlashing();
    }
  }

  #startFlashing(): void {
    document.title = ALERT_TITLE;
    let on = true;
    faviconLink().href = ALERT_ICON;

    this.#flashTimer = setInterval(() => {
      on = !on;
      faviconLink().href = on ? ALERT_ICON : IDLE_ICON;
    }, 700) as unknown as number;
  }

  #stopFlashing(): void {
    if (this.#flashTimer !== null) {
      clearInterval(this.#flashTimer);
      this.#flashTimer = null;
    }
    document.title = BASE_TITLE;
    faviconLink().href = IDLE_ICON;
  }

  /**
   * Two short tones, synthesised rather than shipped as an audio file — it is a
   * few lines, avoids a network request, and cannot fail to load.
   */
  #chime(): void {
    if (this.#muted || this.#audio === null) return;

    const ctx = this.#audio;
    const now = ctx.currentTime;

    for (const [index, frequency] of [660, 880].entries()) {
      const oscillator = ctx.createOscillator();
      const gain = ctx.createGain();

      oscillator.type = 'sine';
      oscillator.frequency.value = frequency;

      const start = now + index * 0.14;
      gain.gain.setValueAtTime(0.0001, start);
      gain.gain.exponentialRampToValueAtTime(0.18, start + 0.02);
      gain.gain.exponentialRampToValueAtTime(0.0001, start + 0.13);

      oscillator.connect(gain).connect(ctx.destination);
      oscillator.start(start);
      oscillator.stop(start + 0.15);
    }
  }
}
